import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { buildSearchParams } from '../_helpers/utils';
import { APIResponse } from '../_types/api-responses.types';
import {
  AllInsuranceFlowsResponse,
  CancelInsuranceFlowContractRequest,
  CancelInsuranceFlowContractResponse,
  CentralisationFlowRequest,
  CentralisationFlowResponse,
  GetInsuranceFlowsParams,
  InsuranceFlowDetailsResponse,
  InsuranceFlowFullHistoryResponse,
  InsuranceFlowsExportParams,
  InsuranceFlowTasksResponse,
  SearchInsuranceFlowsParams,
  SearchInsuranceFlowsResponse,
} from '../_types/insurance-flow.types';
import { TokenPartner } from '../_types/partner.type';
import { AllTasksRequest } from '../_types/proposals.type';
import { Globals } from '../globals';

@Injectable({
  providedIn: 'root',
})
export class InsuranceFlowService {
  baseUrl: string;

  baseHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  constructor(
    private readonly httpClient: HttpClient,
    private readonly globals: Globals
  ) {
    this.baseUrl = `${environment.apiUrl}api/insurance/v1/insurance-flow`;
  }

  get headers() {
    return this.baseHeaders.append(
      'Access-Token',
      (this.globals.getUser() as TokenPartner).token
    );
  }

  getInsuranceFlowDetails(
    id: string
  ): Observable<APIResponse<InsuranceFlowDetailsResponse>> {
    return this.httpClient.get<APIResponse<InsuranceFlowDetailsResponse>>(
      `${this.baseUrl}/details/${id}`,
      {
        headers: this.headers,
      }
    );
  }

  getInsuranceFlows(
    params: GetInsuranceFlowsParams
  ): Observable<APIResponse<AllInsuranceFlowsResponse>> {
    return this.httpClient.post<APIResponse<AllInsuranceFlowsResponse>>(
      this.baseUrl,
      params,
      {
        headers: this.headers,
      }
    );
  }

  /**
   * Type-ahead for the "Affaire" picker: one member's flows, capped and
   * searchable server-side.
   *
   * Much lighter than `getInsuranceFlows`, which returns full flow summaries
   * and has to be asked for an absurd page size to stand in for a search.
   */
  searchInsuranceFlows(
    params: SearchInsuranceFlowsParams
  ): Observable<APIResponse<SearchInsuranceFlowsResponse>> {
    const searchParams = buildSearchParams({ ...params });

    return this.httpClient.get<APIResponse<SearchInsuranceFlowsResponse>>(
      `${this.baseUrl}/search?${searchParams.toString()}`,
      { headers: this.headers }
    );
  }

  getAllTasks(
    params: AllTasksRequest
  ): Observable<APIResponse<InsuranceFlowTasksResponse>> {
    return this.httpClient.post<APIResponse<InsuranceFlowTasksResponse>>(
      `${this.baseUrl}/get/tasks`,
      params,
      {
        headers: this.headers,
      }
    );
  }

  exportInsuranceFlowsCsv(
    params: InsuranceFlowsExportParams
  ): Observable<string> {
    return this.httpClient.post(`${this.baseUrl}/export`, params, {
      headers: this.headers,
      responseType: 'text',
    });
  }

  getFlowFullHistory(
    id: string
  ): Observable<APIResponse<InsuranceFlowFullHistoryResponse>> {
    return this.httpClient.get<APIResponse<InsuranceFlowFullHistoryResponse>>(
      `${this.baseUrl}/${id}/full-history`,
      {
        headers: this.headers,
      }
    );
  }

  sendCentralisationFlow(
    body: CentralisationFlowRequest
  ): Observable<APIResponse<CentralisationFlowResponse>> {
    return this.httpClient.post<APIResponse<CentralisationFlowResponse>>(
      `${this.baseUrl}/centralisation/mark-sent`,
      body,
      { headers: this.headers }
    );
  }

  cancelCentralisationFlow(
    body: CentralisationFlowRequest
  ): Observable<APIResponse<CentralisationFlowResponse>> {
    return this.httpClient.post<APIResponse<CentralisationFlowResponse>>(
      `${this.baseUrl}/centralisation/cancel`,
      body,
      { headers: this.headers }
    );
  }

  cancelContract(
    body: CancelInsuranceFlowContractRequest
  ): Observable<APIResponse<CancelInsuranceFlowContractResponse>> {
    return this.httpClient.post<
      APIResponse<CancelInsuranceFlowContractResponse>
    >(`${this.baseUrl}/contract/cancel`, body, { headers: this.headers });
  }
}
